import { computed, action, observable } from 'mobx';
import { ProductBaseController } from './ProductBaseController';
import { emit, events, on } from 'elc-service-bus';
import { getMobileCarouselSettings } from '../constants/ImageThumbnailCarouselSettings';
import { SECONDS } from '../constants/TimeoutSeconds';
import {
    IProductBriefOrderableViewControllerData,
    IProductBriefProps
} from '../interfaces/IProductBriefOrderable';
import { ComponentTypes } from '../constants/ComponentTypes';

export class ProductBriefViewController extends ProductBaseController {
    @observable private isProductQuickViewOpen: boolean = false;
    @observable public isLoaded: boolean = false;
    @action
    public openProductQuickView = () => {
        this.isProductQuickViewOpen = true;
    };

    @action
    public closeProductQuickView = () => {
        this.isProductQuickViewOpen = false;
    };

    @action
    public onQuickViewButtonClick = async (skuId: string) => {
        this.openProductQuickView();
        emit(events.QUICK_VIEW_TRIGGERED, {
            skuId,
            positionIndex: this.positionIndex
        });
    };

    @computed
    get data(): IProductBriefOrderableViewControllerData {
        const {
            product,
            selectedSku,
            largeMedia,
            productBriefImageCarouselSettings,
            isMobileImageCarouselEnabled,
            isDesktopImageCarouselEnabled,
            isProductQuickViewOpen,
            isShortDescriptionEnabled
        } = this;

        return {
            productUrl: this.selectedProductUrl,
            displayName: product.displayNameDecoded,
            productBadge: product.productBadge,
            selectedSku,
            skuBadge: selectedSku.skuBadge,
            largeMedia,
            alternateImage: selectedSku.defaultAlternateMediumImage.src,
            product,
            productBriefImageCarouselSettings,
            isMobileImageCarouselEnabled,
            isDesktopImageCarouselEnabled,
            isProductQuickViewOpen,
            isShortDescriptionEnabled
        };
    }

    @action
    public initialize = async (props: IProductBriefProps) => {
        const { product } = props;

        this.setProduct(product);
        this.positionIndex = props.positionIndex || 0;
        this.isLoaded = true;
        this.selectBestShade();
    };

    @computed
    private get productBriefImageCarouselSettings() {
        return (
            this.imageCarouselSettings && this.imageCarouselSettings[ComponentTypes.PRODUCT_BRIEF]
        );
    }

    @computed
    private get isMobileImageCarouselEnabled() {
        const mobileImageCarouselSettings =
            this.productBriefImageCarouselSettings &&
            getMobileCarouselSettings(this.productBriefImageCarouselSettings);

        return mobileImageCarouselSettings && mobileImageCarouselSettings.enabled;
    }

    @computed
    private get isDesktopImageCarouselEnabled() {
        return (
            this.productBriefImageCarouselSettings && this.productBriefImageCarouselSettings.enabled
        );
    }

    @computed
    private get productBriefShortDescriptionSettings() {
        return this.shortDescriptionSettings &&
            this.shortDescriptionSettings.overrides &&
            this.shortDescriptionSettings.overrides[ComponentTypes.PRODUCT_BRIEF]
            ? {
                  ...this.shortDescriptionSettings,
                  ...this.shortDescriptionSettings.overrides[ComponentTypes.PRODUCT_BRIEF]
              }
            : this.shortDescriptionSettings;
    }

    @computed
    private get isShortDescriptionEnabled() {
        const shortDescriptionSettings = this.productBriefShortDescriptionSettings;

        return shortDescriptionSettings && shortDescriptionSettings.enabled;
    }

    @on(events.CART_UPDATED)
    public cartUpdated = () => {
        this.closeProductQuickView();
    };

    @on(events.FAVORITE_STATUS_UPDATED)
    public favoriteUpdated = () => {
        setTimeout(() => this.closeProductQuickView(), SECONDS.ONE);
    };

    @on(events.CART_FAILURE)
    public cartFailure = () => {
        this.closeProductQuickView();
    };
}
